import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { RecaptchaComponent } from 'ng-recaptcha';
import { Router, NavigationEnd } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { UtilsService } from 'app/shared/utils.service';
import { GetUserQuery } from './API.service';
import { UserService } from './shared/user.service';
import { DomainFrameworkService } from './shared/domain-framework.service';
import { FileService } from 'app/shared/file.service';
import { ScoreService } from './shared/score.service';
import { ThemeService } from './shared/services/theme.service';
import { NavigationContextService } from './shared/services/navigation-context.service';
import { FULL_PAGE_LOADER } from './shared/full-page-loader/full-page-loader.constant';
import { CollectionPrefetchService } from './collection/collection-prefetch.service';
import { initScrollbarReveal } from './shared/utils/scroll-visibility.util';
@Component({
  selector: 'cygov-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  showSidenav = true;
  showLegend = false;
  showFeatureLegend = false;
  isOnboarding = false;
  user: GetUserQuery;
  routeChangedSubscription: Subscription;
  favIcon: HTMLLinkElement = document.querySelector('#favIcon');
  // The index.html splash is the ONLY loader a cold entry shows. It used to be dismissed on the
  // first NavigationEnd, which handed over to the landing screen's own full-page loader and read as
  // two loaders in a row. It now holds until that screen's data lands, so app bootstrap and the
  // screen's load happen behind one loader. The screen's own overlay still renders underneath at
  // z-index 500 and is simply never seen; both end in the same tick.
  private splashDismissed = false;
  private splashHeld = false;
  private splashResolvePending = false;
  private splashFallbackTimer: ReturnType<typeof setTimeout>;
  private splashResolveTimer: ReturnType<typeof setTimeout>;
  private coldLoadIdleSubscription: Subscription;

  // outer bound while holding: must outlast the screen loader's own safety timeout, or the splash
  // would lift first and expose the very handover this is here to avoid
  private static readonly SPLASH_HOLD_FALLBACK_MS = FULL_PAGE_LOADER.SAFETY_TIMEOUT_MS + 2000;
  private removeScrollbarReveal: () => void;

  constructor(
    private router: Router,
    private titleService: Title,
    private metaService: Meta,
    private userService: UserService,
    private domainFrameworkService: DomainFrameworkService,
    private fileService: FileService,
    private scoreService: ScoreService,
    private themeService: ThemeService,
    private navigationContext: NavigationContextService,
    private collectionPrefetch: CollectionPrefetchService,
    private ngZone: NgZone
  ) {
    const companyConfig = UtilsService.companyConfig;
    // Set index page title and meta
    this.titleService.setTitle(companyConfig.title);
    this.metaService.addTag({ name: 'viewport', content: 'width=device-width, initial-scale=1' });
    this.metaService.addTag({ name: 'og:title', content: companyConfig.ogTitle });
    this.metaService.addTag({ name: 'og:description', content: companyConfig.ogDescription });
    this.metaService.addTag({ name: 'og:image', content: companyConfig.ogImage });
    this.favIcon.href = companyConfig.favicon;

    this.splashFallbackTimer = setTimeout(() => this.hideSplash(), 15000);

    this.routeChangedSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(val => {
        // check if user is authenticate in every route change
        if (val instanceof NavigationEnd) {
          this.navigationContext.markNavigationEnd(val.urlAfterRedirects || val.url);
          // no-op unless environment.collectionDataPrefetchEnabled is on
          this.collectionPrefetch.onNavigation(val.urlAfterRedirects || val.url);
          this.resolveSplash();
          this.setConfig(val);
          this.initDomainFramework();
        }
      });
  }

  async ngOnInit() {
    // https://github.com/DethAriel/ng-recaptcha/issues/123#issuecomment-426112101

    RecaptchaComponent.prototype.ngOnDestroy = function () {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
    };
    // Reveal auto-hiding scrollbars while scrolling (outside Angular zone to
    // avoid change detection on every scroll event). See src/styles.scss.
    this.ngZone.runOutsideAngular(() => {
      this.removeScrollbarReveal = initScrollbarReveal();
    });
    // Initiate the file service to load the Enums from s3. These enums are required before we go to any component
    await this.fileService.init();
    this.userService.observeUserInteraction();
    this.scoreService.initScoreService();
  }

  setConfig(val): void {
    this.user = this.userService.getCurrentUserSync();
    this.isOnboarding = val.url.includes('onboarding');
    this.isAuthenticated = !!this.user;
    this.showSidenav = !!this.user;
    if (this.user) {
      this.themeService.initializeFromUser(this.user);
    }
    this.showLegend =
      (val.url.includes('clients') && !val.url.includes('user-settings')) ||
      val.url.includes('upperdeck') ||
      val.url.includes('multi-entity');
    this.showFeatureLegend = val.url.includes('collection');
  }

  // Initialize domain domain allowed routes, domain average score and domain frameworks settings
  // this should be run after user logged-in.
  initDomainFramework(): void {
    if (this.isAuthenticated) {
      this.domainFrameworkService.init();
    }
  }

  ngOnDestroy(): void {
    this.routeChangedSubscription.unsubscribe();
    clearTimeout(this.splashFallbackTimer);
    clearTimeout(this.splashResolveTimer);
    if (this.coldLoadIdleSubscription) {
      this.coldLoadIdleSubscription.unsubscribe();
    }
    if (this.removeScrollbarReveal) {
      this.removeScrollbarReveal();
    }
  }

  /**
   * The router creates the landing component while activating the route, but its ngOnInit — and so
   * its claimColdLoad() call — only runs on the change detection pass that follows, which is after
   * this event. Checking for a claim here would always find none and dismiss the splash straight
   * away, which is the double-loader handover this exists to remove. setTimeout defers the decision
   * by one macrotask; Angular's tick drains ahead of it, so by then the claim is recorded.
   */
  private resolveSplash(): void {
    if (this.splashDismissed || this.splashHeld || this.splashResolvePending) {
      return;
    }
    this.splashResolvePending = true;
    this.splashResolveTimer = setTimeout(() => this.applySplashDecision(), 0);
  }

  /**
   * No claim means nothing follows the splash, so it goes immediately — exactly as it always did
   * for screens that are not wired to the full-page loader. A claim means the screen is loading
   * behind the splash, so the splash stays until that screen's data lands.
   */
  private applySplashDecision(): void {
    this.splashResolvePending = false;
    if (this.splashDismissed) {
      return;
    }
    if (!this.navigationContext.hasActiveColdLoad()) {
      this.hideSplash();
      return;
    }
    this.splashHeld = true;
    clearTimeout(this.splashFallbackTimer);
    this.splashFallbackTimer = setTimeout(() => this.hideSplash(), AppComponent.SPLASH_HOLD_FALLBACK_MS);
    this.coldLoadIdleSubscription = this.navigationContext.coldLoadIdle$.subscribe(() => this.hideSplash());
  }

  /** Fades out and removes the index.html splash; safe to call more than once. */
  private hideSplash(): void {
    if (this.splashDismissed) {
      return;
    }
    this.splashDismissed = true;
    clearTimeout(this.splashFallbackTimer);
    clearTimeout(this.splashResolveTimer);
    if (this.coldLoadIdleSubscription) {
      this.coldLoadIdleSubscription.unsubscribe();
    }
    const splash = document.getElementById('cygov-splash');
    if (!splash) {
      return;
    }
    splash.classList.add('is-hiding');
    setTimeout(() => splash.remove(), 250);
  }
}
